/**
 * QueryParameterInputs Component
 *
 * Displays input fields for query parameters in API endpoints.
 * Handles query parameters with `in: "query"` from the OpenAPI specification.
 *
 * Requirements:
 * - 13.2: Display input fields for query parameters (`in: "query"`)
 * - 13.4: Mark required parameters
 * - 13.7: Display parameter descriptions as help text
 */

import { EndpointParameter } from '../types';

export interface QueryParameterInputsProps {
  /** Query parameters from the endpoint */
  parameters: EndpointParameter[];
  /** Current parameter values */
  values: Record<string, string>;
  /** Callback when parameter values change */
  onChange: (values: Record<string, string>) => void;
}

/**
 * QueryParameterInputs component renders input fields for query parameters.
 *
 * @param parameters - Array of endpoint parameters (only query params will be rendered)
 * @param values - Current values for query parameters
 * @param onChange - Callback function when values change
 */
export const QueryParameterInputs = ({
  parameters,
  values,
  onChange,
}: QueryParameterInputsProps) => {
  // Filter to only query parameters (in: "query")
  const queryParams = parameters.filter((param) => param.in === 'query');

  // If no query parameters, don't render anything
  if (queryParams.length === 0) {
    return null;
  }

  /**
   * Handle input change for a specific parameter
   */
  const handleChange = (paramName: string, value: string) => {
    onChange({
      ...values,
      [paramName]: value,
    });
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Query Parameters</h3>

      <div className="space-y-3">
        {queryParams.map((param) => (
          <div key={param.name} className="space-y-1">
            <label
              htmlFor={`query-param-${param.name}`}
              className="block text-sm font-medium text-gray-700"
            >
              {param.name}
              {param.required && (
                <span className="text-red-500 ml-1" aria-label="required">
                  *
                </span>
              )}
            </label>

            <input
              id={`query-param-${param.name}`}
              type="text"
              value={values[param.name] || ''}
              onChange={(e) => handleChange(param.name, e.target.value)}
              required={param.required}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={`Enter ${param.name}`}
            />

            {param.description && <p className="text-sm text-gray-500 mt-1">{param.description}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default QueryParameterInputs;
